import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../src/functions/useLanguage';
import { wt } from '../i18n';
import { websitePath } from '../lib/routes';

interface Post {
  slug: string;
  dateISO: string;
  tag: string;
  titleEn: string;
  excerptEn: string;
  titleFr: string;
  excerptFr: string;
  titleDe: string;
  excerptDe: string;
  titlePt: string;
  excerptPt: string;
  titleEs: string;
  excerptEs: string;
  readMins: number;
}

const POSTS: Post[] = [
  {
    slug: 'the-vital-check',
    dateISO: '2026-05-06',
    tag: 'Labs',
    titleEn: 'The Vital Check: Monitoring Your Pet’s Vitals & Behavior',
    excerptEn:
      'A practical guide to tracking heart rate, breathing, temperature, and daily behaviors so you can spot changes early and share clear trends with your vet.',
    titleFr: 'Le contrôle vital : suivre les signes vitaux et le comportement de votre animal',
    excerptFr:
      'Un guide pratique pour suivre le rythme cardiaque, la respiration, la température et les comportements afin de détecter rapidement les changements et partager des tendances claires avec le vétérinaire.',
    titleDe: 'Der Vital-Check: Vitalwerte und Verhalten Ihres Haustiers beobachten',
    excerptDe:
      'Ein praxisnaher Leitfaden zu Herzfrequenz, Atmung, Temperatur und Verhalten, damit Sie Veränderungen früh erkennen und klare Trends mit dem Tierarzt teilen können.',
    titlePt: 'O check vital: monitorizar sinais vitais e comportamento do seu pet',
    excerptPt:
      'Um guia prático para acompanhar frequência cardíaca, respiração, temperatura e comportamentos, detectando mudanças cedo e partilhando tendências com o veterinário.',
    titleEs: 'El control vital: monitorea las constantes y el comportamiento de tu mascota',
    excerptEs:
      'Una guía práctica para seguir frecuencia cardiaca, respiración, temperatura y conductas, detectar cambios temprano y compartir tendencias claras con tu veterinario.',
    readMins: 7,
  },
  {
    slug: 'the-care-circle',
    dateISO: '2026-04-23',
    tag: 'Roomies',
    titleEn: 'The Care Circle: Coordinating Your Pet’s Health Team',
    excerptEn:
      'Coordinate family, sitters, and your vet with a shared care plan so meds, symptoms, and routines stay aligned.',
    titleFr: 'Le cercle de soins : coordonner l’équipe de santé de votre animal',
    excerptFr:
      'Coordonnez famille, pet sitters et vétérinaire avec un plan partagé pour aligner traitements, symptômes et routines.',
    titleDe: 'Der Pflegekreis: Das Gesundheitsteam Ihres Haustiers koordinieren',
    excerptDe:
      'Koordinieren Sie Familie, Sitter und Tierarzt mit einem gemeinsamen Plan, damit Medikation, Symptome und Routinen abgestimmt bleiben.',
    titlePt: 'O círculo de cuidados: coordenar a equipa de saúde do seu animal',
    excerptPt:
      'Coordene família, cuidadores e veterinário com um plano partilhado para alinhar medicação, sintomas e rotinas.',
    titleEs: 'El círculo de cuidado: coordinar el equipo de salud de tu mascota',
    excerptEs:
      'Coordina familia, cuidadores y veterinario con un plan compartido para alinear medicación, síntomas y rutinas.',
    readMins: 6,
  },
  {
    slug: 'the-lab-story',
    dateISO: '2026-04-10',
    tag: 'Labs',
    titleEn: 'The Lab Story: Reading Your Pet’s Results with Confidence',
    excerptEn:
      'Read bloodwork and urinalysis with confidence by focusing on trends and pairing results with daily symptoms in KodaCare.',
    titleFr: 'L’histoire des analyses : comprendre les résultats de votre animal',
    excerptFr:
      'Lisez les analyses avec confiance en regardant les tendances et en reliant les résultats aux symptômes quotidiens dans KodaCare.',
    titleDe: 'Die Labor-Story: Ergebnisse Ihres Haustiers sicher lesen',
    excerptDe:
      'Lesen Sie Blut- und Urinwerte sicher, indem Sie Trends betrachten und Ergebnisse mit Alltagssymptomen in KodaCare verknüpfen.',
    titlePt: 'A história dos exames: interpretar resultados do seu animal',
    excerptPt:
      'Interprete exames com confiança ao focar tendências e ligar resultados a sintomas diários no KodaCare.',
    titleEs: 'La historia de los análisis: entender los resultados de tu mascota',
    excerptEs:
      'Interpreta los análisis con confianza al mirar tendencias y conectar resultados con síntomas diarios en KodaCare.',
    readMins: 7,
  },
  {
    slug: 'the-habit-loop',
    dateISO: '2026-03-28',
    tag: 'Routines',
    titleEn: 'The Habit Loop: Building Daily Pet Care Routines That Stick',
    excerptEn:
      'Build simple routines for meds, meals, hydration, and activity that your whole care circle can keep.',
    titleFr: 'La boucle d’habitudes : des routines de soin qui tiennent',
    excerptFr:
      'Créez des routines simples pour médicaments, repas, hydratation et activité, faciles à tenir pour toute l’équipe.',
    titleDe: 'Die Gewohnheitsschleife: Routinen für die tägliche Tierpflege',
    excerptDe:
      'Einfache Routinen für Medikamente, Mahlzeiten, Flüssigkeit und Aktivität, die das ganze Team durchhält.',
    titlePt: 'O ciclo do hábito: rotinas diárias que funcionam',
    excerptPt:
      'Rotinas simples para medicação, refeições, hidratação e atividade que toda a equipa consegue manter.',
    titleEs: 'El ciclo del hábito: rutinas diarias que sí se mantienen',
    excerptEs:
      'Rutinas simples para medicación, comidas, hidratación y actividad que todo el equipo puede mantener.',
    readMins: 5,
  },
];

const TAG_COLORS: Record<string, string> = {
  'New Norms': 'bg-primary-brown/15 text-primary-brown',
  Roomies: 'bg-primary-brown/15 text-primary-brown',
  Labs: 'bg-primary-brown/15 text-primary-brown',
  Routines: 'bg-primary-brown/15 text-primary-brown',
};

type Lang = 'English' | 'French' | 'German' | 'Portuguese' | 'Spanish';

const BLOG_LOCALE_BY_LANGUAGE: Record<Lang, string> = {
  English: 'en-GB',
  French: 'fr-FR',
  German: 'de-DE',
  Portuguese: 'pt-PT',
  Spanish: 'es-ES',
};

function formatBlogDate(dateISO: string, language: Lang): string {
  const locale = BLOG_LOCALE_BY_LANGUAGE[language] ?? 'en-GB';
  return new Date(`${dateISO}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function getTitle(post: Post, language: Lang): string {
  const map: Record<Lang, string> = {
    English: post.titleEn,
    French: post.titleFr,
    German: post.titleDe,
    Portuguese: post.titlePt,
    Spanish: post.titleEs,
  };
  return map[language] ?? post.titleEn;
}

function getExcerpt(post: Post, language: Lang): string {
  const map: Record<Lang, string> = {
    English: post.excerptEn,
    French: post.excerptFr,
    German: post.excerptDe,
    Portuguese: post.excerptPt,
    Spanish: post.excerptEs,
  };
  return map[language] ?? post.excerptEn;
}

export function Blog() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const activeLanguage = language as Lang;

  const getTagLabel = (tag: string): string => {
    const keyMap: Record<string, Parameters<typeof wt>[1]> = {
      'New Norms': 'blog.tag.newNorms',
      Roomies: 'blog.tag.roomies',
      Labs: 'blog.tag.labs',
      Routines: 'blog.tag.routines',
    };
    return keyMap[tag] ? t(keyMap[tag]) : tag;
  };

  return (
    <main className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-black text-[#0d2744] mb-4">
            {t('blog.heading')}
          </h1>
          <p className="text-gray-500 text-sm max-w-lg mx-auto">{t('blog.subheading')}</p>
        </div>

        {/* Featured post */}
        <div
          className="relative rounded-[2rem] overflow-hidden bg-[#0d2744] text-white p-10 mb-10 cursor-pointer group"
          onClick={() => navigate(websitePath(`/blog/${POSTS[0].slug}`))}
        >
          <div
            className="absolute inset-0 opacity-10"
            style={{ background: 'radial-gradient(circle at 80% 50%, rgba(176, 122, 74, 0.7) 0%, transparent 60%)' }}
          />
          <div className="relative z-10 max-w-2xl">
            <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-4 ${TAG_COLORS[POSTS[0].tag] ?? 'bg-primary-brown/15 text-primary-brown'}`}>
              {getTagLabel(POSTS[0].tag)}
            </span>
            <h2 className="text-2xl lg:text-3xl font-black mb-4 group-hover:underline">
              {getTitle(POSTS[0], activeLanguage)}
            </h2>
            <p className="text-primary-brown/80 text-sm leading-relaxed mb-6">
              {getExcerpt(POSTS[0], activeLanguage)}
            </p>
            <div className="flex items-center gap-4 text-xs text-primary-brown/80">
              <span>{formatBlogDate(POSTS[0].dateISO, activeLanguage)}</span>
              <span>·</span>
              <span>{POSTS[0].readMins} {t('blog.minRead')}</span>
              <span className="ml-2 text-primary-brown font-bold">{t('blog.readMore')}</span>
            </div>
          </div>
        </div>

        {/* Remaining posts grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {POSTS.slice(1).map((post) => (
            <article
              key={post.slug}
              className="bg-white border border-slate-100 rounded-3xl p-7 transition-all cursor-pointer group"
              onClick={() => navigate(websitePath(`/blog/${post.slug}`))}
            >
              <span
                className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-4 ${TAG_COLORS[post.tag] ?? 'bg-gray-100 text-gray-600'}`}
              >
                {getTagLabel(post.tag)}
              </span>
              <h3 className="text-lg font-bold text-[#0d2744] mb-3 group-hover:underline leading-snug">
                {getTitle(post, activeLanguage)}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                {getExcerpt(post, activeLanguage)}
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span>{formatBlogDate(post.dateISO, activeLanguage)}</span>
                <span>·</span>
                <span>{post.readMins} {t('blog.minRead')}</span>
              </div>
              <p className="text-primary-brown text-xs font-bold mt-3">{t('blog.readMore')}</p>
            </article>
          ))}
        </div>
      </div>
    </main>
  );
}
